import * as React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { useModal } from 'mui-modal-provider';

import { accessRequestPopupStorageKey } from '../../components/Dialogs/AccessRequestAccepted/AccessRequestAcceptedDialog';
import AccessRequestDescDialog from '../../components/Dialogs/AccessRequestDesc/AccessRequestDescDialog';
import { ListAccessRequestsV2Dialog } from '../../components/Dialogs/ListAccessRequestsV2/ListAccessRequestsV2Dialog';
import { ListBlockedUsersDialog } from '../../components/Dialogs/ListBlockedUsers/ListBlockedUsersDialog';
import PageTitle from '../../components/PageTitle/PageTitle';
import AccessRequestTable from '../../components/Tables/AccessRequest/AccessRequest';
import { UserAccessRequestResultValue, VenueListAccessRequetsFilter } from '../../graphql/API';
import { useAppSelector } from '../../redux/hooks';
import Icons from '../../themes/icons';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import { ProductRequestsHeader } from './ProductRequestsHeader';
import { extractSubscriptionPlan } from '../SubscriptionPlans/common/helper';
import { brandPromotionLimit } from '../../utils/reservations';
import { useReservationLimitInPeriodContext } from '../../contexts/ReservationsLimitInPeriodContext';

export type AccessRequestPageView = 'grid' | 'list';

const ViewItem: React.FC<{
  title: string;
  icon: React.ReactNode;
  view: AccessRequestPageView;
  activeView: AccessRequestPageView;
  handleView: (val: AccessRequestPageView) => void;
}> = ({ title, icon, view, activeView, handleView }) => (
  <Box
    display={'flex'}
    alignItems={'center'}
    gap={'8px'}
    color={view === activeView ? '#765BC6' : '#757575'}
    sx={{ cursor: 'pointer' }}
    onClick={() => {
      handleView('list');
    }}
  >
    {icon}
    <span>{title}</span>
  </Box>
);

const AccessRequest: React.FC = () => {
  const lastLoggedInVenueID = localStorage.getItem('lastLoggedInVenueID');
  const { showModal } = useModal();

  const venueState = useAppSelector((state) => state.venue.value);
  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const [filters, setFilters] = React.useState<Partial<VenueListAccessRequetsFilter> | undefined>({
    sortBy: 'createdAt',
    sortDirection: 'desc',
    status: UserAccessRequestResultValue.requested,
    marketRegions: [],
  });

  const [view, setView] = React.useState<AccessRequestPageView>('grid');
  const [totalRequests, setTotalRequests] = React.useState(0);
  const [selectedOfferIds, setSelectedOfferIds] = React.useState<number[]>([]);

  React.useEffect(() => {
    const _venueID = lastLoggedInVenueID ? JSON.parse(lastLoggedInVenueID) : '';
    if (_venueID && venueState && _venueID !== venueState?.id)
      localStorage.setItem(accessRequestPopupStorageKey, JSON.stringify(false));
  }, [lastLoggedInVenueID, venueState, venueState?.id]);

  const handleClickDesc = React.useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      e.preventDefault();

      showModal(AccessRequestDescDialog, { width: '600px', height: 'unset' });
    },
    [showModal],
  );

  const handleClickList = React.useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      e.preventDefault();

      showModal(ListAccessRequestsV2Dialog, {
        titleComponent: <></>,
      });
    },
    [showModal],
  );

  const handleBlockedUsersList = React.useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      e.preventDefault();

      showModal(ListBlockedUsersDialog, {
        titleComponent: <Box textAlign='center'>Blocked users</Box>,
        width: '790px',
      });
    },
    [showModal],
  );

  const handleView = (val: AccessRequestPageView) => {
    setView(val);
  };

  const { subscription } = useAppSelector((state) => state.subscription.value);

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');


    return plan;
  }, [subscription]);

  const { tags: venueTags } = useAppSelector((state) => state.venue.value) || {};

  const { totalWaitingAndShippedReservations, totalReservedAndCheckedInReservations } =
    useReservationLimitInPeriodContext() || {};

  const totalReservations = React.useMemo(
    () =>
      (isOnlineBrand
        ? totalWaitingAndShippedReservations
        : totalReservedAndCheckedInReservations) || 0,
    [isOnlineBrand, totalWaitingAndShippedReservations, totalReservedAndCheckedInReservations],
  );

  const isPromotionLimitHit = React.useMemo(
    () =>
      venueTags?.includes(VENUE_MANUAL_TAGS.BrandPlans) &&
      (subscriptionPlan === 'basic' || subscriptionPlan === 'starter') &&
      totalReservations >= brandPromotionLimit[subscriptionPlan],
    [subscriptionPlan, totalReservations, venueTags],
  );

  return (
    <Box sx={{ backgroundColor: 'white', flex: 1, p: 4 }}>
      <PageTitle
        containerAlignItems='flex-start'
        titleComponent={
          <Box>
            <Typography fontSize={'20px'} fontWeight='bold' mr={3}>
              {venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online)
                ? `Product Requests `
                : 'Access Request'}
              {venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) && (
                <Typography component={'span'} fontWeight={'bold'} fontSize={'20px'}>
                  ({totalRequests})
                </Typography>
              )}
            </Typography>
            {venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) && (
              <Typography fontSize={'15px'} fontWeight={500} mt={1}>
                These members would like to promote your brand!
              </Typography>
            )}
          </Box>
        }
        sideComponent={
          <>
            {!venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) && (
              <Box
                component='a'
                href='#'
                onClick={handleClickDesc}
                sx={{ fontSize: '0.8rem', color: 'black' }}
              >
                What is access request?
              </Box>
            )}
          </>
        }
        rightComponent={
          <Stack direction='row' spacing={2}>
            {isOnlineBrand ? (
              <ProductRequestsHeader
                filters={filters}
                setFilters={setFilters}
                view={view}
                handleView={handleView}
                selectedOfferIds={selectedOfferIds}
                onSelectedOfferIdsChange={setSelectedOfferIds}
              />
            ) : (
              <>
                <ViewItem
                  title='Grid'
                  view={'grid'}
                  activeView={view}
                  handleView={() => {
                    handleView('grid');
                  }}
                  icon={<Icons.GridIcon />}
                />
                <ViewItem
                  title='List'
                  view={'list'}
                  activeView={view}
                  handleView={() => {
                    handleView('list');
                  }}
                  icon={<Icons.ListIcon />}
                />
              </>
            )}
          </Stack>
        }
      />

      <Box mt={1}>
        <AccessRequestTable
          filters={filters}
          setFilters={setFilters}
          view={view}
          isPromotionLimitHit={isPromotionLimitHit}
          height={isOnlineBrand ? 'calc(100vh - 225px)' : 'calc(100vh - 260px)'}
          handleClickList={handleClickList}
          handleBlockedUsersList={handleBlockedUsersList}
          setTotalRequests={setTotalRequests}
          selectedOfferIds={selectedOfferIds}
        />
      </Box>
    </Box>
  );
};

export default AccessRequest;
